import React, { useRef, useLayoutEffect, useState } from "react";
import PropTypes from 'prop-types';
import { WonIcon, LostIcon, OnHlodIcon } from "../../../../components/Icons/Icons";

const measureText = (text, fontSize = 12, fontWeight = 600, fontFamily = "Noto Sans, sans-serif") => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
    return ctx.measureText(text).width;
};

const MIN_ARROW_WIDTH = 80;
const H_PADDING = 24;
const NOTCH = 10;

const computeRows = (stages, containerWidth) => {
    if (!containerWidth || !stages.length) return [];

    const rows = [];
    let currentRow = [];
    let currentRowWidth = 0;

    stages.forEach((stage, i) => {
        const labelWidth = stage.isIcon
            ? 48
            : Math.max(
                measureText(stage.label) + H_PADDING * 2 + NOTCH,
                MIN_ARROW_WIDTH
            );

        if (currentRowWidth + labelWidth > containerWidth + 1 && currentRow.length > 0) {
            rows.push(currentRow);
            currentRow = [];
            currentRowWidth = 0;
        }

        currentRow.push({ ...stage, measuredWidth: labelWidth, originalIndex: i });
        currentRowWidth += labelWidth;
    });

    if (currentRow.length) rows.push(currentRow);

    return rows.map((row, rowIndex) => {
        const isLastRow = rowIndex === rows.length - 1;
        const isSingleItem = row.length === 1;

        if (isLastRow && isSingleItem) {
            return row.map((s) => ({ ...s, arrowWidth: s.measuredWidth }));
        }

        const iconStagesInRow = row.filter(s => s.isIcon);
        const arrowStagesInRow = row.filter(s => !s.isIcon);

        const totalIconWidth = iconStagesInRow.reduce((sum, s) => sum + s.measuredWidth, 0);
        const totalArrowMeasured = arrowStagesInRow.reduce((sum, s) => sum + s.measuredWidth, 0);
        const availableForArrows = containerWidth - totalIconWidth;
        const scale = totalArrowMeasured > 0 ? availableForArrows / totalArrowMeasured : 1;

        return row.map((s) => ({
            ...s,
            arrowWidth: s.isIcon ? s.measuredWidth : s.measuredWidth * scale,
        }));
    });
};

const ArrowPipeline = ({
    stages,
    currentStage,
    onStageClick,
    completedColor = "#0d6efd",
    upcomingColor = "#d3d3d3",
    arrowHeight = 40,
    editableType,
    edit,
    displayData,
    pipeLineStage,
    oldPipeline,
}) => {
    const engagedIndex = stages.findIndex(item => item.optionKey === "won");
    const splitIndex = engagedIndex !== -1 ? engagedIndex + 1 : stages.length;

    const allStages = [
        ...stages.slice(0, splitIndex).map(s => ({ ...s, isIcon: false })),
        ...stages.slice(splitIndex).map(s => ({ ...s, isIcon: true })),
    ];

    const containerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [hoveredOriginalIndex, setHoveredOriginalIndex] = useState(null);

    useLayoutEffect(() => {
        if (!containerRef.current) return;
        const ro = new ResizeObserver(() => {
            if (containerRef.current)
                setContainerWidth(containerRef.current.offsetWidth);
        });
        ro.observe(containerRef.current);
        setContainerWidth(containerRef.current.offsetWidth);
        return () => ro.disconnect();
    }, []);

    const rows = computeRows(allStages, containerWidth);
    const totalArrowStages = splitIndex;
    const isInteractive = edit && editableType;

    const rowGap = 8;
    const totalHeight = rows.length > 0
        ? rows.length * arrowHeight + (rows.length - 1) * rowGap
        : arrowHeight;

    const activeCeiling = hoveredOriginalIndex !== null
        ? hoveredOriginalIndex
        : oldPipeline !== null
            ? pipeLineStage.findIndex(s => s.value === oldPipeline)
            : pipeLineStage.findIndex(s => s.value === currentStage);

    // ── Collect icon positions so we can overlay HTML divs on top ──
    const iconOverlays = [];
    rows.forEach((row, rowIndex) => {
        const rowY = rowIndex * (arrowHeight + rowGap);
        let xCursor = 0;
        row.forEach((stage) => {
            const x = xCursor;
            xCursor += stage.arrowWidth;
            if (stage.isIcon) {
                iconOverlays.push({ ...stage, x, rowY });
            }
        });
    });

    return (
        <div ref={containerRef} style={{ width: "100%", position: "relative" }}>
            <svg
                height={totalHeight}
                width={containerWidth || "100%"}
                style={{ fontFamily: "'Noto Sans', sans-serif", display: "block" }}
                onMouseLeave={() => setHoveredOriginalIndex(null)}
            >
                {rows.map((row, rowIndex) => {
                    const rowY = rowIndex * (arrowHeight + rowGap);
                    let xCursor = 0;

                    return (
                        <g key={rowIndex} transform={`translate(0, ${rowY})`}>
                            {row.map((stage, colIndex) => {
                                const { value, label, arrowWidth, originalIndex, isIcon } = stage;
                                const x = xCursor;
                                xCursor += arrowWidth;

                                // Icon slots: just a transparent placeholder rect in the SVG
                                if (isIcon) {
                                    return (
                                        <rect
                                            key={value}
                                            x={x}
                                            y={0}
                                            width={arrowWidth}
                                            height={arrowHeight}
                                            fill="transparent"
                                        />
                                    );
                                }

                                // ── Arrow stage ──
                                const isGlobalFirst = originalIndex === 0;
                                const isGlobalLast = originalIndex === totalArrowStages - 1;
                                const isLastInRow = colIndex === row.length - 1;
                                const nextIsIcon = row[colIndex + 1]?.isIcon;

                                const flatLeft = isGlobalFirst;
                                const flatRight = (isGlobalLast && isLastInRow) || nextIsIcon;

                                let points;
                                if (flatLeft && flatRight) {
                                    points = `${x},0 ${x + arrowWidth},0 ${x + arrowWidth},${arrowHeight} ${x},${arrowHeight}`;
                                } else if (flatLeft) {
                                    points = `${x},0 ${x + arrowWidth - NOTCH},0 ${x + arrowWidth},${arrowHeight / 2} ${x + arrowWidth - NOTCH},${arrowHeight} ${x},${arrowHeight}`;
                                } else if (flatRight) {
                                    points = `${x},0 ${x + arrowWidth},0 ${x + arrowWidth},${arrowHeight} ${x},${arrowHeight} ${x + NOTCH},${arrowHeight / 2}`;
                                } else {
                                    points = `${x},0 ${x + arrowWidth - NOTCH},0 ${x + arrowWidth},${arrowHeight / 2} ${x + arrowWidth - NOTCH},${arrowHeight} ${x},${arrowHeight} ${x + NOTCH},${arrowHeight / 2}`;
                                }

                                const isFilled = originalIndex <= activeCeiling;
                                const isDisabled = displayData?.pipeline_stage === 8;
                                const fillColor = isFilled ? completedColor : upcomingColor;
                                const textFill = isFilled ? "#ffffff" : "#a1a1a1";

                                const textX = x + arrowWidth / 2;
                                const words = label.split(" ");
                                let textContent;
                                if (words.length <= 2) {
                                    textContent = <tspan>{words.join(" ")}</tspan>;
                                } else {
                                    const mid = Math.ceil(words.length / 2);
                                    textContent = (
                                        <>
                                            <tspan x={textX} dy="-5">{words.slice(0, mid).join(" ")}</tspan>
                                            <tspan x={textX} dy="14">{words.slice(mid).join(" ")}</tspan>
                                        </>
                                    );
                                }

                                return (
                                    <g
                                        key={value}
                                        onClick={() => {
                                            if (isInteractive && !isDisabled)
                                                onStageClick(value);
                                        }}
                                        onMouseEnter={() => {
                                            if (isInteractive) setHoveredOriginalIndex(originalIndex);
                                        }}
                                        style={{ cursor: isInteractive && !isDisabled ? "pointer" : "default" }}
                                    >
                                        <polygon points={points} fill={fillColor} />
                                        <text
                                            x={textX}
                                            y={arrowHeight / 2}
                                            textAnchor="middle"
                                            fill={textFill}
                                            fontSize="12"
                                            fontWeight="600"
                                            dominantBaseline="middle"
                                        >
                                            {textContent}
                                        </text>
                                    </g>
                                );
                            })}
                        </g>
                    );
                })}
            </svg>

            {/* ── Icon overlays rendered as HTML divs, absolutely positioned over the SVG ── */}
            {iconOverlays.map((stage) => {
                const { value, label, arrowWidth, rowY } = stage;
                const iconLabel = label === "Won" ? "Won Deal"
                    : label === "Lost" ? "Lost Deal"
                        : "On Hold";

                return (
                    <div
                        key={value}
                        title={iconLabel}
                        className={`${value === currentStage
                            ? `${label}-Icon-Color`
                            : `${label}-Icon-Colors`
                            } step-container ${isInteractive ? "hover-enabled" : ""}`}
                        style={{
                            position: "absolute",
                            left: stage.x,
                            top: rowY,
                            width: arrowWidth,
                            height: arrowHeight,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: isInteractive ? "pointer" : "default",
                            boxSizing: "border-box",
                        }}
                        role="button"
                        tabIndex={0}
                        onMouseEnter={() => setHoveredOriginalIndex(null)}
                        onMouseLeave={() => setHoveredOriginalIndex(null)}
                        onKeyDown={(e) => {
                            if (isInteractive && (e.key === "Enter" || e.key === " "))
                                onStageClick(value);
                        }}
                        onClick={() => {
                            if (isInteractive) onStageClick(value);
                        }}
                    >
                        {label === "Won" ? <WonIcon />
                            : label === "Lost" ? <LostIcon />
                                : (label === "on-Hold" || label === "On-Hold") ? <OnHlodIcon />
                                    : null}
                    </div>
                );
            })}
        </div>
    );
};

ArrowPipeline.propTypes = {
    stages: PropTypes.arrayOf(PropTypes.object).isRequired,
    currentStage: PropTypes.number,
    onStageClick: PropTypes.func,
    completedColor: PropTypes.string,
    upcomingColor: PropTypes.string,
    arrowHeight: PropTypes.number,
    oldPipeline: PropTypes.number,
    edit: PropTypes.bool,
    editableType: PropTypes.bool,
    displayData: PropTypes.object,
    pipeLineStage: PropTypes.array,
};

export default ArrowPipeline;